import classNames from "classnames";
import { NumberFormatter } from "../../../utils/NumberFormatter";
import { useTranslation } from "react-i18next";
import Tag, { type TagVariant } from "../Tag/Tag";

import classes from "./StockIndicator.module.less";
import { styled } from "@mui/material";
import { C_IN_STOCK_AURA } from "../icon/Colors";
import { NumberFormatter as NF } from "~/utils/NumberFormatter";

interface StockIndicatorProps {
  className?: string;
  level: StockLevel;
  changeText?: string;
  checkAvailability?: boolean;
}

export const StockIndicatorIcon = styled("div")<JSX.IntrinsicElements["div"] & { level: StockLevel; size?: number }>(({
  theme,
  level,
  size = 12,
}) => {
  let background;
  let border;
  let iconSize = size;
  let padding;
  switch (level) {
    case StockLevel.IN_STOCK:
      background = theme.palette.primary.main;
      border = `2px solid ${C_IN_STOCK_AURA}`;
      padding = 2;
      break;
    case StockLevel.PARTIAL_STOCK:
      background = `linear-gradient(to top, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.text.secondary} 51%, ${theme.palette.text.secondary} 100%)`;
      iconSize = 8;
      padding = 0;
      break;
    case StockLevel.NOT_IN_STOCK:
      background = theme.palette.text.secondary;
      iconSize = 8;
      padding = 0;
      break;
  }

  const dimension = "var(--size)";

  return {
    "::after": {
      content: '""',
      display: "block",
      width: "100%",
      height: "100%",
      background,
      border,
      borderRadius: "50%",
    },
    "--size": `${iconSize}px`,
    display: "inline-block",
    width: dimension,
    height: dimension,
    borderRadius: "50%",
    flex: "none",
    padding: `${padding}px`,
  };
});

export enum StockLevel {
  IN_STOCK,
  PARTIAL_STOCK,
  NOT_IN_STOCK,
}

export const stockConfig = {
  [StockLevel.NOT_IN_STOCK]: {
    translationKey: "stockIndicator.missing",
  },
  [StockLevel.PARTIAL_STOCK]: {
    translationKey: "stockIndicator.partial",
  },
  [StockLevel.IN_STOCK]: {
    translationKey: "stockIndicator.exists",
  },
};

export const StockIndicator = (props: StockIndicatorProps) => {
  const { level, className, changeText } = props;
  const { t } = useTranslation();

  const { translationKey } = stockConfig[level];
  const cls = classNames(classes.stockIndicator, className);

  return (
    <div className={cls}>
      <StockIndicatorIcon level={level} />
      <span className={classes.text} data-test-id={"stock-indicator"}>
        {changeText ? changeText : t(translationKey)}
      </span>
      {props.checkAvailability && (
        <span className={classes.checkAvailability}>{t("stockIndicator.check.availability")}</span>
      )}
    </div>
  );
};

interface StockIndicatorV2Props {
  stock: number;
  className?: string;
}

const StockIndicatorV2 = ({ stock, className }: StockIndicatorV2Props) => {
  const { t } = useTranslation();
  const inStock = stock > 0;

  let variant: TagVariant;
  let level;
  let label;
  if (inStock) {
    variant = "primary";
    level = StockLevel.IN_STOCK;
    label = NF.getDefaultFormatter().format(stock) + " " + t(stockConfig[level].translationKey);
  } else {
    variant = "default";
    level = StockLevel.NOT_IN_STOCK;
    label = t(stockConfig[level].translationKey);
  }

  return (
    <Tag variant={variant} className={className}>
      <StockIndicatorIcon level={level} />
      {label}
    </Tag>
  );
};

StockIndicator.V2 = styled(StockIndicatorV2)({
  display: "inline-flex",
  gap: "4px",
});
interface SimpleStockIndicatorProps {
  className?: string;
  inStock?: boolean;
  checkAvailability?: boolean;
}

export const SimpleStockIndicator = ({ className, inStock, checkAvailability }: SimpleStockIndicatorProps) => (
  <StockIndicator
    className={className}
    level={inStock ? StockLevel.IN_STOCK : StockLevel.NOT_IN_STOCK}
    checkAvailability={checkAvailability}
  />
);

interface QuantityStockIndicatorProps {
  className?: string;
  quantity: number;
}

export const QuantityStockIndicator = ({ className, quantity }: QuantityStockIndicatorProps) => {
  const level = quantity > 0 ? StockLevel.IN_STOCK : StockLevel.NOT_IN_STOCK;

  const { t } = useTranslation();
  const label =
    quantity > 0
      ? t("stockIndicator.existsWithQty", { amount: NumberFormatter.getFormatter().format(quantity) })
      : t("stockIndicator.missing");

  return <StockIndicator className={className} level={level} changeText={label} />;
};

export function getStockLevel(availableStock: number = 0, requestedQty: number = 0) {
  if (!availableStock) {
    return StockLevel.NOT_IN_STOCK;
  }
  if (availableStock >= requestedQty) {
    return StockLevel.IN_STOCK;
  } else {
    return StockLevel.PARTIAL_STOCK;
  }
}

export const BackInStockIndicator = () => {
  const { t } = useTranslation();

  return <Tag variant={"backInStock"}>{t("recentlyViewProducts.backInStock")}</Tag>;
};

export default StockIndicator;
